import axios from "axios";
import { toast } from "react-toastify";
let redirectInProgress = false; // prevents multiple toasts/redirects

// Deliberate, actionable client errors that must reach the calling
// component instead of resolving into the 1s global toast (App.js
// autoClose={1000}) — that duration is unsuitable for an error the user
// must read and act on. Mirrors the case-400-only opt-out this set now
// replaces (originally `StatusNoteRequiredError` alone, CLNOTE-F003) and
// extends it to the checklist-comments error names (docs/TRD/
// checklist-comments.md §3.7, D6, CLCMT-F003). Every other 4xx in the app
// keeps today's behaviour byte-for-byte.
const INLINE_ERROR_NAMES = new Set([
  "StatusNoteRequiredError",
  "CommentInvalidError",
  "CommentForbiddenError",
  "CommentNotFoundError",
  "CommentStaleError",
  // Checklist rate import: rejected so the wizard can render message and details inline.
  "RateSheetInvalidError",
  "RateImportRowsInvalidError",
  "RateImportForbiddenError",
  "RepriceScopeInvalidError",
  "RateRunFailedError",
]);
const makeApiCall = async (
  method,
  url,
  data = {},
  headers = {},
  responseType = ""
) => {
  const commonHeaders = {
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  };
  const headersWithContentType = {
    ...commonHeaders,
  };
  const config = {
    method,
    url,
    headers: headersWithContentType,
    data,
    responseType,
  };
  axios.interceptors.response.use(
    function (response) {
      // Do something with response data
      return response;
    },
    function (err) {
      const status = err.response?.status || 500;
      switch (status) {
        // authentication (token related issues)
        case 401: {
          // A wrong-password 401 from the login request itself is not a
          // session timeout — there is no session to expire and nothing to
          // redirect to (the user is already on the sign-in screen), so it
          // must not trigger the storage-clear/redirect flow below or reuse
          // its "session expired" wording.
          if (err.response?.data?.message === 'Invalid Credentials!') {
            return toast.error("Authentication failed. Invalid credentials.");
          }
            // Clear all stored data
          if (!redirectInProgress) {
            redirectInProgress = true;
            toast.error("Your session has expired. You’ll be redirected to sign in.");
            setTimeout(() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = "/";
                }, 4000);
          }
          return true;
        }
        // forbidden (permission related issues)
        case 403: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("forbidden permission");
        }

        // bad request
        case 400: {
          // Deliberate, actionable client errors are re-thrown so the calling
          // component can render them inline (originally the checklist
          // status-change note requirement alone, CLNOTE-F003; extended to
          // the checklist-comments names by CLCMT-F003 — see
          // INLINE_ERROR_NAMES above). Every other 400 keeps today's
          // behaviour byte-for-byte.
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("Bad request");
        }

        // not found
        case 404: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("Not Found");
        }

        // conflict
        case 409: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("Conflict");
        }

        default: {
          return err
        }

        // generic api error (server related) unexpected
        // case !422:
        // default: {
        //   return toast.error("forbidden permission");
        // }
      }
    }
  );

  return await axios.request(config);
};

// const makeApiCallCopy = async (
//   method,
//   url,
//   data = {},
//   headers = {},
//   responseType = ""
// ) => {
//   const commonHeaders = {
//     Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//   };
//   const headersWithContentType = {
//     ...commonHeaders,
//   };
//   const config = {
//     method,
//     url,
//     headers: headersWithContentType,
//     data,
//     responseType,
//   };
//   axios.interceptors.response.use(
//     function (response) {
//       // Do something with response data
//       return response;
//     },
//     function (err) {
//       const status = err.response?.status || 500;
//       console.log("err", err);
//       switch (status) {
//         // authentication (token related issues)
//         case 401: {
//           return toast.error("Invalid authentication ");
//         }
//         // forbidden (permission related issues)
//         case 403: {
//           return toast.error("forbidden permission");
//         }

//         // bad request
//         case 400: {
//           return toast.error("Bad request");
//         }

//         // not found
//         case 404: {
//           return toast.error("Not Found");
//         }

//         // conflict
//         case 409: {
//           return toast.error("Conflict");
//         }

//         default: {
//           return err.response;
//         }

//         // generic api error (server related) unexpected
//         // case !422:
//         // default: {
//         //   return toast.error("forbidden permission");
//         // }
//       }
//     }
//   );

//   return await axios.request(config);
// };

const makeApiCallWithHeaders = async (method, url, data = {}, headers = {}) => {
  const config = {
    method,
    url,
    headers,
    data,
  };
  axios.interceptors.response.use(
    function (response) {
      // Do something with response data
      return response;
    },
    function (err) {
      const status = err.response?.status || 500;
      switch (status) {
        // authentication (token related issues)
        case 401: {
          return toast.error("Invalid authentication ");
        }
        // forbidden (permission related issues)
        case 403: {
          return toast.error("forbidden permission");
        }

        // bad request
        case 400: {
          return toast.error("Bad request");
        }

        // not found
        case 404: {
          return toast.error("Not Found");
        }

        // conflict
        case 409: {
          return toast.error("Conflict");
        }

        // unprocessable
        // case 422: {
        //   return toast.error("Unprocessable content");
        // }

        // generic api error (server related) unexpected
        // default: {
        //   return toast.error("forbidden permission");
        // }
      }
    }
  );

  return await axios.request(config);
};

export { makeApiCall, makeApiCallWithHeaders };
